import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ChevronLeft, X } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  WorldHeader,
  LearningPathCard,
  WORLD_EXPLORER_BY_ID,
  WORLD_EXPLORER_IDS,
  getWorldExplorer,
} from '../components/worldExplorer';
import { PremiumUnlockModal } from '../components/explore';
import { usePremium, isPremiumMockEnabled } from '../utils/premiumStore';
import { trackUserAction, trackMiniAppEvent, trackSafetyEvent, trackLearning } from '../utils/analytics';
import { searchPreflight, getPreflightFailureKind, SEARCH_PREFLIGHT_SERVICE_ERROR_MESSAGE } from '../utils/searchPreflight';
import { notifySearchLicenseRequired } from '../utils/searchLicenseNotify';
import { getCurrentUser } from '../services/firebase';
import { miniAppRegistry } from '../miniapps';
import { getMiniAppForLearningPath } from '../utils/worldPathMiniApp';
import {
  getGuidedLesson,
  getPathLessonCounts,
  getPathLessons as getPathGuidedLessons,
  hasGuidedLesson,
} from '../utils/guidedLessonRegistry';
import { getPathLessons as getPathLessonIds } from '../utils/skillGraph';
import { compareByAgeBandMatch, useAgeBand } from '../utils/ageBand';
import {
  getSkillForLesson,
  getSkillById,
  isSkillUnlocked,
} from '../utils/skillGraph';
import {
  makeLessonKey,
  markLessonComplete,
  markLessonStarted,
  buildScoreFromEngineDetail,
  setLastVisitedWorld,
  useProgressSnapshot,
  useReviewQueue,
} from '../utils/progressStore';
import {
  isLessonAccessible,
  PREMIUM_PARENT_COPY,
  PREMIUM_CTA_LABEL,
} from '../config/accessConfig';
import { flagshipPathSortKey } from '../config/launchConfig';
import { loadSearchFeatureEnabled } from '../utils/searchFeaturePreferencesStore';

const pageStyle = {
  minHeight: '100vh',
  background: 'var(--color-bg)',
  padding: '20px 18px 48px',
  maxWidth: 1040,
  margin: '0 auto',
  boxSizing: 'border-box',
};

const backRowStyle = {
  marginBottom: 20,
};

const backLinkStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--color-primary)',
  textDecoration: 'none',
};

const sectionTitleStyle = {
  fontSize: 'clamp(1.2rem, 3.5vw, 1.45rem)',
  fontWeight: 800,
  color: 'var(--color-text)',
  marginBottom: 6,
};

const sectionSubStyle = {
  fontSize: 15,
  color: 'var(--color-text)',
  opacity: 0.65,
  marginBottom: 22,
  lineHeight: 1.45,
  maxWidth: 560,
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
  gap: 18,
};

const landingIntroStyle = {
  textAlign: 'left',
  marginBottom: 28,
};

const landingTitleStyle = {
  fontSize: 'clamp(1.5rem, 4vw, 1.85rem)',
  fontWeight: 800,
  color: 'var(--color-text)',
  marginBottom: 10,
  letterSpacing: '-0.02em',
};

const landingDescStyle = {
  fontSize: 16,
  lineHeight: 1.5,
  color: 'var(--color-text)',
  opacity: 0.75,
  maxWidth: 520,
};

const worldPickCardStyle = {
  display: 'block',
  borderRadius: 18,
  padding: 22,
  textDecoration: 'none',
  border: '1px solid var(--color-border)',
  boxShadow: '0 4px 18px var(--color-shadow)',
  transition: 'transform 0.18s ease, box-shadow 0.18s ease',
};

const unknownStyle = {
  textAlign: 'center',
  padding: '48px 24px',
  color: 'var(--color-text)',
};

const adventureSectionStyle = {
  marginTop: 32,
  padding: '20px 18px 24px',
  borderRadius: 18,
  border: '1px solid var(--color-border)',
  background: 'var(--color-surface, var(--color-bg))',
  boxShadow: '0 8px 28px var(--color-shadow)',
};

const adventureHeaderStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 16,
  marginBottom: 16,
};

const adventureTitleWrapStyle = {
  flex: 1,
  minWidth: 0,
};

const adventureKickerStyle = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--color-primary)',
  marginBottom: 4,
  opacity: 0.9,
};

const adventureTitleStyle = {
  fontSize: 'clamp(1.05rem, 3vw, 1.2rem)',
  fontWeight: 800,
  color: 'var(--color-text)',
  margin: 0,
  lineHeight: 1.3,
};

const closeAdventureBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '8px 12px',
  borderRadius: 10,
  border: '1px solid var(--color-border)',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  flexShrink: 0,
};

function WorldPickerCard({ worldId, topicsBase }) {
  const world = WORLD_EXPLORER_BY_ID[worldId];
  if (!world) return null;
  return (
    <Link
      to={`${topicsBase}/${worldId}`}
      style={{
        ...worldPickCardStyle,
        background: world.bannerGradient,
      }}
      className="world-picker-card"
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = '0 12px 32px var(--color-shadow)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 18px var(--color-shadow)';
      }}
    >
      <div style={{ fontSize: 48, marginBottom: 8 }} aria-hidden>{world.icon}</div>
      <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--color-text)', marginBottom: 8 }}>
        {world.title}
      </div>
      <div style={{ fontSize: 14, lineHeight: 1.45, color: 'var(--color-text)', opacity: 0.78 }}>
        Open learning paths
      </div>
    </Link>
  );
}

const TopicsPage = () => {
  const { worldId, locale } = useParams();
  const navigate = useNavigate();
  const [startBusy, setStartBusy] = useState(false);
  const [activeAdventure, setActiveAdventure] = useState(null);
  const adventureRef = useRef(null);
  const progress = useProgressSnapshot();
  const reviewQueue = useReviewQueue();
  const [activeAgeBand] = useAgeBand();
  const [premiumActive, setPremiumActive] = usePremium();
  const [premiumModalPath, setPremiumModalPath] = useState(null);
  const startAdventureRef = useRef(null);

  const handleUnlockPremium = useCallback((path) => {
    setPremiumModalPath(path || null);
  }, []);

  const confirmPremiumUnlock = useCallback(() => {
    setPremiumActive(true);
    const targetPath = premiumModalPath;
    setPremiumModalPath(null);
    toast.success('Premium unlocked (test mode). All lessons are open on this device.');
    if (targetPath) {
      setTimeout(() => {
        startAdventureRef.current?.(targetPath);
      }, 0);
    }
  }, [premiumModalPath, setPremiumActive]);

  const topicsBase = locale ? `/${locale}/topics` : '/topics';
  const searchBase = locale ? `/${locale}/s` : '/s';

  const world = worldId ? getWorldExplorer(worldId) : null;

  useEffect(() => {
    setActiveAdventure(null);
  }, [worldId]);

  useEffect(() => {
    if (world?.id) setLastVisitedWorld(world.id);
  }, [world?.id]);

  useEffect(() => {
    if (!worldId) trackLearning.worldExplorerOpen();
  }, [worldId]);

  useEffect(() => {
    if (world?.id) trackLearning.worldOpen(world.id, world.title);
  }, [world?.id, world?.title]);

  useEffect(() => {
    if (!activeAdventure || !adventureRef.current) return;
    adventureRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [activeAdventure]);

  const openSearchAdventure = useCallback(
    async (path, lessonKey) => {
      const q = String(path.adventureQuery || '').trim();
      if (!q) return;
      if (!loadSearchFeatureEnabled()) {
        toast.error('Search is turned off on this device. A parent can turn it on in Settings.');
        return;
      }

      setStartBusy(true);
      trackUserAction.searchSubmitted('web');
      const user = getCurrentUser();
      const firebaseId = user ? user.uid : null;

      try {
        const preflight = await searchPreflight(q);
        if (preflight.reason === 'auth_required') {
          toast.error('Sign in with your Kiddoware account to search.');
          navigate('/auth');
          return;
        }
        if (preflight.reason === 'license_required') {
          notifySearchLicenseRequired();
          return;
        }
        const failureKind = getPreflightFailureKind(preflight);
        if (failureKind === 'service_error') {
          toast.error(SEARCH_PREFLIGHT_SERVICE_ERROR_MESSAGE);
          return;
        }
        if (failureKind === 'keyword_blocked') {
          toast.error(`Search not allowed: "${preflight.search_keyword}". Try a different query.`);
          trackSafetyEvent.keywordBlocked(q, firebaseId);
          return;
        }
        if (lessonKey) markLessonStarted(lessonKey);
        navigate(`${searchBase}?q=${encodeURIComponent(q)}&type=web`);
      } catch (err) {
        console.error('Search preflight failed:', err);
        toast.error('Could not verify search access. Please try again.');
      } finally {
        setStartBusy(false);
      }
    },
    [navigate, searchBase],
  );

  const startAdventure = useCallback(
    async (path) => {
      if (!world) return;
      if (!isLessonAccessible(world.id, path.id, { hasPremium: premiumActive })) {
        // Show the premium unlock prompt instead of silently no-op'ing.
        setPremiumModalPath(path);
        return;
      }

      const completedSet = new Set(progress.completedLessons);
      const pathLessonIds = getPathLessonIds(world.id, path.id);
      const activeLessonId =
        pathLessonIds.find((lid) => !completedSet.has(makeLessonKey(world.id, lid))) ||
        pathLessonIds[0] ||
        path.id;

      const skill = getSkillForLesson(world.id, activeLessonId);
      if (skill && !isSkillUnlocked(world.id, skill.id, completedSet)) {
        toast('Finish the prerequisite first.');
        return;
      }

      const lessonKey = makeLessonKey(world.id, activeLessonId);

      if (hasGuidedLesson(world.id, activeLessonId)) {
        markLessonStarted(lessonKey);
        trackLearning.pathEngage({
          world_id: world.id,
          path_id: path.id,
          engage_type: 'guided_lesson',
          lesson_id: activeLessonId,
        });
        navigate(`${topicsBase}/${world.id}/lesson/${activeLessonId}`);
        return;
      }

      const mini = getMiniAppForLearningPath(world.id, path.id);
      if (mini) {
        const Engine = miniAppRegistry[mini.app];
        if (Engine) {
          markLessonStarted(lessonKey);
          trackLearning.pathEngage({
            world_id: world.id,
            path_id: path.id,
            engage_type: 'mini_app',
            mini_app: mini.app,
            lesson_id: activeLessonId,
          });
          setActiveAdventure({
            app: mini.app,
            props: {
              ...mini.props,
              onComplete: (detail) => {
                markLessonComplete(lessonKey, buildScoreFromEngineDetail(detail));
              },
            },
            pathTitle: path.title,
            pathId: path.id,
            worldId: world.id,
          });
          trackMiniAppEvent.launched(mini.props.activityId);
          return;
        }
      }

      trackLearning.pathEngage({
        world_id: world.id,
        path_id: path.id,
        engage_type: 'search_adventure',
        lesson_id: activeLessonId,
      });
      await openSearchAdventure(path, lessonKey);
    },
    [world, openSearchAdventure, navigate, topicsBase, progress.completedLessons, premiumActive],
  );

  useEffect(() => {
    startAdventureRef.current = startAdventure;
  }, [startAdventure]);

  if (worldId && !world) {
    return (
      <div style={pageStyle}>
        <div style={unknownStyle}>
          <h1 style={{ marginBottom: 12 }}>This world is not available yet</h1>
          <p style={{ opacity: 0.75, marginBottom: 24 }}>
            Try Animals, Robotics, Math Adventures, or Space Missions.
          </p>
          <Link to={`${topicsBase}/explorer`} className="btn btn-primary">
            See worlds
          </Link>
        </div>
      </div>
    );
  }

  if (!worldId) {
    return (
      <div style={pageStyle}>
        <div
          style={{
            ...backRowStyle,
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px 20px',
            alignItems: 'center',
          }}
        >
          <Link to={locale ? `/${locale}` : '/'} style={backLinkStyle}>
            <ChevronLeft size={22} aria-hidden />
            Home
          </Link>
          <Link to={topicsBase} style={backLinkStyle}>
            All lessons
          </Link>
        </div>
        <div style={landingIntroStyle}>
          <h1 style={landingTitleStyle}>World Explorer</h1>
          <p style={landingDescStyle}>
            Choose a learning world. Each one has short paths you can finish at your own pace — calm, clear, and built for ages 5–12.
          </p>
        </div>
        <div style={gridStyle}>
          {WORLD_EXPLORER_IDS.map((id) => (
            <WorldPickerCard key={id} worldId={id} topicsBase={topicsBase} />
          ))}
        </div>
      </div>
    );
  }

  const ActiveEngine = activeAdventure ? miniAppRegistry[activeAdventure.app] : null;

  return (
    <div style={pageStyle}>
      <div style={backRowStyle}>
        <Link to={`${topicsBase}/explorer`} style={backLinkStyle}>
          <ChevronLeft size={22} aria-hidden />
          All worlds
        </Link>
      </div>

      <WorldHeader world={world} />

      {(() => {
        const findPathForLesson = (lessonId) =>
          world.learningPaths.find((p) =>
            getPathLessonIds(world.id, p.id).includes(lessonId),
          ) || null;
        const dueInWorld = reviewQueue
          .filter(({ lessonKey }) => lessonKey.startsWith(`${world.id}:`))
          .map(({ lessonKey }) => {
            const [, lessonId] = lessonKey.split(':');
            const path = findPathForLesson(lessonId);
            const guided = getGuidedLesson(world.id, lessonId);
            return path ? { lessonKey, lessonId, path, lessonTitle: guided?.title || path.title } : null;
          })
          .filter(Boolean);
        if (dueInWorld.length === 0) return null;
        const first = dueInWorld[0];
        return (
          <section
            aria-labelledby="review-queue-heading"
            style={{
              marginBottom: 22,
              padding: '14px 16px',
              borderRadius: 14,
              border: '1px solid var(--color-border)',
              background: 'rgba(245, 158, 11, 0.10)',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 12,
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                id="review-queue-heading"
                style={{
                  fontSize: 13,
                  fontWeight: 800,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: 'var(--color-text)',
                  opacity: 0.7,
                  marginBottom: 2,
                }}
              >
                Time for a quick review
              </div>
              <div style={{ fontSize: 15, color: 'var(--color-text)', lineHeight: 1.4 }}>
                {dueInWorld.length === 1
                  ? `Refresh "${first.lessonTitle}" — short and helpful, no streaks.`
                  : `${dueInWorld.length} lessons are ready to review. Start with "${first.lessonTitle}".`}
              </div>
            </div>
            <button
              type="button"
              style={{
                padding: '10px 14px',
                borderRadius: 12,
                border: 'none',
                background: 'var(--color-primary)',
                color: '#fff',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 2px 10px var(--color-shadow)',
                flexShrink: 0,
              }}
              onClick={() => navigate(`${topicsBase}/${world.id}/lesson/${first.lessonId}`)}
            >
              Review {first.lessonTitle}
            </button>
          </section>
        );
      })()}

      <section aria-labelledby="learning-paths-heading">
        <h2 id="learning-paths-heading" style={sectionTitleStyle}>
          Learning paths
        </h2>
        <p style={sectionSubStyle}>
          Pick a path below. Each adventure opens a focused, kid-friendly session you can explore step by step.
        </p>
        <div style={gridStyle}>
          {[...world.learningPaths]
            .sort((a, b) => {
              const ageCmp = compareByAgeBandMatch(activeAgeBand)(a, b);
              if (ageCmp !== 0) return ageCmp;
              return flagshipPathSortKey(world.id, a.id) - flagshipPathSortKey(world.id, b.id);
            })
            .map((path) => {
              const completedSet = new Set(progress.completedLessons);
              const locked = !isLessonAccessible(world.id, path.id, { hasPremium: premiumActive });

              const pathLessonIds = getPathLessonIds(world.id, path.id);
              const pathLessons = getPathGuidedLessons(world.id, path.id);
              const counts = getPathLessonCounts(world.id, path.id);

              const completedInPath = pathLessonIds.filter((lid) =>
                completedSet.has(makeLessonKey(world.id, lid)),
              ).length;
              const totalInPath = pathLessonIds.length;
              const completed = totalInPath > 0 && completedInPath === totalInPath;
              const pathProgressPct =
                totalInPath > 0 ? Math.round((completedInPath / totalInPath) * 100) : path.progress ?? 0;

              const activeLessonId =
                pathLessonIds.find((lid) => !completedSet.has(makeLessonKey(world.id, lid))) ||
                pathLessonIds[0] ||
                path.id;
              const guidedActive = getGuidedLesson(world.id, activeLessonId);
              const totalMinutes = pathLessons.reduce(
                (sum, l) => sum + (Number(l?.estimatedMinutes) || 0),
                0,
              );
              const displayDuration = totalMinutes > 0 ? totalMinutes : guidedActive?.estimatedMinutes ?? path.durationMinutes;

              const skill = getSkillForLesson(world.id, activeLessonId);
              const prereqLocked = !!skill && !isSkillUnlocked(world.id, skill.id, completedSet);
              let prereqMessage = '';
              if (prereqLocked && skill) {
                const reqTitles = (skill.requires || [])
                  .map((rid) => getSkillById(world.id, rid)?.title)
                  .filter(Boolean);
                prereqMessage = reqTitles.length
                  ? `Finish "${reqTitles.join('", "')}" first`
                  : 'Finish the previous skill first';
              }

              const lessonCount = counts.available > 0 ? counts.available : null;
              const reviewDue = pathLessonIds.some((lid) =>
                reviewQueue.some((r) => r.lessonKey === makeLessonKey(world.id, lid)),
              );

              const startedSet = new Set(progress.startedLessons || []);
              const lessonStatuses = pathLessonIds.map((lid) => {
                const key = makeLessonKey(world.id, lid);
                const mastery = progress.lessonMastery?.[key];
                const guided = getGuidedLesson(world.id, lid);
                const lessonTitle =
                  guided?.title ||
                  lid
                    .split('-')
                    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                    .join(' ');
                return {
                  lessonId: lid,
                  lessonTitle,
                  completed: completedSet.has(key),
                  started: startedSet.has(key),
                  reviewCount: mastery?.reviewCount || 0,
                };
              });

              const prereqWhyMessage = prereqLocked
                ? 'For ages up to 12, sticking to the order keeps each new idea built on something solid. We surface the next step the moment the earlier one is done.'
                : '';

              const displayPath = {
                ...path,
                durationMinutes: displayDuration,
                progress: pathProgressPct,
              };
              return (
                <LearningPathCard
                  key={path.id}
                  path={displayPath}
                  onStartAdventure={startAdventure}
                  onUnlockPremium={isPremiumMockEnabled() ? handleUnlockPremium : null}
                  disabled={startBusy}
                  locked={locked}
                  completed={completed}
                  parentPremiumMessage={PREMIUM_PARENT_COPY}
                  premiumCtaLabel={PREMIUM_CTA_LABEL}
                  activeAgeBand={activeAgeBand}
                  lessonCount={lessonCount}
                  lessonStatuses={lessonStatuses}
                  prereqLocked={prereqLocked}
                  prereqMessage={prereqMessage}
                  prereqWhyMessage={prereqWhyMessage}
                  reviewDue={reviewDue}
                />
              );
            })}
        </div>
      </section>

      {activeAdventure && ActiveEngine ? (
          <section
            ref={adventureRef}
            aria-labelledby="active-adventure-title"
            style={adventureSectionStyle}
          >
            <div style={adventureHeaderStyle}>
              <div style={adventureTitleWrapStyle}>
                <div style={adventureKickerStyle}>Your adventure</div>
                <h2 id="active-adventure-title" style={adventureTitleStyle}>
                  {activeAdventure.pathTitle}
                </h2>
              </div>
              <button
                type="button"
                style={closeAdventureBtnStyle}
                onClick={() => setActiveAdventure(null)}
                aria-label="Close adventure"
              >
                <X size={18} aria-hidden />
                Close
              </button>
            </div>
            <ActiveEngine key={`${activeAdventure.worldId}-${activeAdventure.pathId}`} {...activeAdventure.props} />
          </section>
      ) : null}

      {isPremiumMockEnabled() ? (
        <PremiumUnlockModal
          open={!!premiumModalPath}
          pathTitle={premiumModalPath?.title || ''}
          onConfirm={confirmPremiumUnlock}
          onClose={() => setPremiumModalPath(null)}
        />
      ) : null}
    </div>
  );
};

export default TopicsPage;
